import { t } from "@lingui/macro";
import type { ResumeDto } from "@reactive-resume/dto";
import { DEFAULT_TEMPLATE, isTemplate } from "@reactive-resume/utils";
import type { SyntheticEvent } from "react";
import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import type { LoaderFunction } from "react-router";
import { redirect } from "react-router";

import { applyTemplateThemeToData } from "@/client/constants/template-presets";
import { queryClient } from "@/client/libs/query-client";
import { findResumeById } from "@/client/services/resume";
import { useBuilderStore } from "@/client/stores/builder";
import { usePageStore } from "@/client/stores/page";
import { useResumeStore } from "@/client/stores/resume";

export const BuilderPage = () => {
  const [ready, setReady] = useState(false);
  const frame = useBuilderStore((state) => state.frame.ref);

  const setFrameRef = useBuilderStore((state) => state.frame.setRef);

  const resume = useResumeStore((state) => state.resume);

  const syncToArtboard = usePageStore((state) => state.syncToArtboard);

  const sendResume = useCallback(
    (target?: HTMLIFrameElement | null) => {
      const current = target ?? frame;

      current?.contentWindow?.postMessage(
        {
          type: "SET_RESUME",
          payload: resume.data,
        },
        "*",
      );
    },
    [frame, resume.data],
  );

  const handleLoad = useCallback(
    (event: SyntheticEvent<HTMLIFrameElement>) => {
      const target = event.currentTarget;

      sendResume(target);

      window.requestAnimationFrame(() => {
        /*
         * Deuxième synchronisation
         * après initialisation de
         * l’application artboard.
         */
        sendResume(target);

        target.contentWindow?.postMessage(
          {
            type: "RESET_VIEW",
          },
          "*",
        );

        syncToArtboard();
      });
    },
    [sendResume, syncToArtboard],
  );

  useEffect(() => {
    sendResume();
    syncToArtboard();
  }, [resume.data, sendResume, syncToArtboard]);

  useEffect(() => setReady(false), [frame]);

  useEffect(() => {
    const receive = (event: MessageEvent) => {
      if (event.source !== frame?.contentWindow || event.origin !== window.location.origin) return;
      if (event.data?.type === "ARTBOARD_READY") {
        sendResume();
        syncToArtboard();
      }
      if (event.data?.type === "LENGTH_PAGES" && event.data.payload?.pages > 0) setReady(true);
    };
    window.addEventListener("message", receive);
    return () => window.removeEventListener("message", receive);
  }, [frame, sendResume, syncToArtboard]);

  return (
    <>
      <Helmet>
        <title>
          {resume.title} - {t`Reactive Resume`}
        </title>
      </Helmet>

      <div className="relative h-full min-h-0">
        {!ready && (
          <div
            role="status"
            className="absolute inset-0 z-10 flex items-center justify-center gap-3 bg-[#eeecfb] text-xs text-slate-600"
          >
            <span
              aria-hidden
              className="size-4 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600 motion-reduce:animate-none"
            />
            {t`Chargement de l’aperçu…`}
          </div>
        )}
        <iframe
          ref={setFrameRef}
          onLoad={handleLoad}
          title={resume.id}
          src="/artboard/builder"
          className="block h-full min-h-0 w-full border-0 bg-white"
        />
      </div>
    </>
  );
};

export const builderLoader: LoaderFunction<ResumeDto> = async ({ params }) => {
  try {
    const id = params.id!;

    const resume = await queryClient.fetchQuery({
      queryKey: ["resume", { id }],
      queryFn: () =>
        findResumeById({
          id,
        }),
    });

    useResumeStore.setState({
      resume,
    });

    // Un CV sur un modèle retiré du catalogue passe sur le modèle par défaut.
    if (!resume.locked && !isTemplate(resume.data.metadata.template)) {
      useResumeStore.getState().setData(applyTemplateThemeToData(resume.data, DEFAULT_TEMPLATE));
    }

    useResumeStore.temporal.getState().clear();

    return resume;
  } catch {
    return redirect("/client/user-space/resumes");
  }
};
